import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  Button,
  Checkbox,
  Field,
  Form,
  H4,
  Text,
  Tooltip,
} from '@payaca/ambrosia';
import {
  HIGHLIGHTED_STRING_OUTLINE_STYLE,
  SELECTED_PANELS_OUTLINE_STYLE,
} from '@payaca/components/solarRoofView/SolarRoofView';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MapLegendItem } from './MapLegend';

type MapLayersButtonProps = {
  showPanelsLayer: boolean;
  setShowPanelsLayer: (show: boolean) => void;
  hasAnalysedImagery: boolean;
  showAnalysedImagery: boolean;
  setShowAnalysedImagery: (show: boolean) => void;
  hasFluxOverlay: boolean;
  showFluxOverlay: boolean;
  setShowFluxOverlay: (show: boolean) => void;
};

const IrradianceLegend: FC = () => (
  <div className="flex items-center gap-2">
    <Text as="span" level="supporting">
      Less sun
    </Text>
    <div
      aria-hidden
      className="h-2 flex-1 rounded-full"
      style={{
        background:
          'linear-gradient(to right, #0d0887, #5402a3, #8b0aa5, #b93289, #db5c68, #f48849, #febc2b, #f0f921)',
      }}
    />
    <Text as="span" level="supporting">
      More sun
    </Text>
  </div>
);

/**
 * Map layer toggles behind a small button in the map's bottom-left corner;
 * the panel opens above it. Ambrosia has no general popover, so this uses
 * Headless UI.
 */
const MapLayersButton: FC<MapLayersButtonProps> = (props) => {
  const {
    showPanelsLayer,
    setShowPanelsLayer,
    hasAnalysedImagery,
    showAnalysedImagery,
    setShowAnalysedImagery,
    hasFluxOverlay,
    showFluxOverlay,
    setShowFluxOverlay,
  } = props;
  const methods = useForm({
    values: {
      panels: showPanelsLayer,
      imagery: showAnalysedImagery,
      irradiance: showFluxOverlay,
    },
  });
  return (
    <Popover>
      <Tooltip placement="right" tooltipContent="Map layers">
        <PopoverButton
          as={Button}
          variant="secondary"
          size="sm"
          aria-label="Map layers"
          className="size-9 justify-center p-0 shadow-sm"
        >
          <UntitledIcon name="eye.3" className="size-5 shrink-0" />
        </PopoverButton>
      </Tooltip>
      <PopoverPanel
        anchor={{ to: 'top start', gap: 8 }}
        portal
        focus
        className="z-popover w-64 space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-md"
      >
        <H4>Map layers</H4>
        <Form
          methods={methods}
          onSubmit={() => undefined}
          className="space-y-3"
        >
          <Controller
            control={methods.control}
            name="panels"
            render={({ field: { name, value, onChange, ...field } }) => (
              <Field name={name}>
                <Checkbox
                  {...field}
                  label="Panels"
                  checked={value}
                  onChange={(event) => {
                    onChange(event.target.checked);
                    setShowPanelsLayer(event.target.checked);
                  }}
                />
                <ul aria-label="Panel outlines" className="mt-2 space-y-1 pl-6">
                  <MapLegendItem
                    outlineStyle={SELECTED_PANELS_OUTLINE_STYLE}
                    label="Selected panels"
                  />
                  <MapLegendItem
                    outlineStyle={HIGHLIGHTED_STRING_OUTLINE_STYLE}
                    label="Selected string"
                  />
                </ul>
              </Field>
            )}
          />
          {hasAnalysedImagery && (
            <Controller
              control={methods.control}
              name="imagery"
              render={({ field: { name, value, onChange, ...field } }) => (
                <Field name={name}>
                  <Checkbox
                    {...field}
                    label="Analysed imagery"
                    checked={value}
                    onChange={(event) => {
                      onChange(event.target.checked);
                      setShowAnalysedImagery(event.target.checked);
                    }}
                  />
                </Field>
              )}
            />
          )}
          {hasFluxOverlay && (
            <Controller
              control={methods.control}
              name="irradiance"
              render={({ field: { name, value, onChange, ...field } }) => (
                <Field name={name}>
                  <Checkbox
                    {...field}
                    label="Sun exposure"
                    checked={value}
                    onChange={(event) => {
                      onChange(event.target.checked);
                      setShowFluxOverlay(event.target.checked);
                    }}
                  />
                </Field>
              )}
            />
          )}
          {hasFluxOverlay && showFluxOverlay && <IrradianceLegend />}
        </Form>
      </PopoverPanel>
    </Popover>
  );
};

export default MapLayersButton;
